/**
 * Mock interactive setup screen for competition manager quiz.
 * Matches the real Setup page layout exactly.
 */

import { useState, useCallback } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

export interface MockDivision {
  id: string;
  name: string;
}

export interface MockSetupHeat {
  id: string;
  name: string;
  divisionId: string;
  maxAttempts: number;
  riders: { id: string; name: string; nickname?: string }[];
}

export interface MockSetupRider {
  id: string;
  name: string;
  nickname?: string;
  level?: string;
}

interface MockSetupScreenProps {
  initialDivisions: MockDivision[];
  initialHeats: MockSetupHeat[];
  availableRiders: MockSetupRider[];
  onDivisionCreate?: (name: string) => void;
  onHeatCreate?: (name: string, divisionId: string) => void;
  onRiderAdded?: (heatId: string, riderId: string) => void;
  onInviteSent?: (email: string, role: string) => void;
}

export function MockSetupScreen({
  initialDivisions,
  initialHeats,
  availableRiders,
  onDivisionCreate,
  onHeatCreate,
  onRiderAdded,
  onInviteSent,
}: MockSetupScreenProps) {
  const [divisions, setDivisions] = useState(initialDivisions);
  const [heats, setHeats] = useState(initialHeats);
  const [expandedHeat, setExpandedHeat] = useState<string | null>(null);

  const [newDivName, setNewDivName] = useState("");
  const [newHeatName, setNewHeatName] = useState("");
  const [newHeatDiv, setNewHeatDiv] = useState("");
  const [newHeatAttempts, setNewHeatAttempts] = useState(1);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("judge");
  const [sentInvites, setSentInvites] = useState<{ email: string; role: string }[]>([]);

  const createDivision = useCallback(() => {
    if (!newDivName.trim()) return;
    const id = `div-${Date.now()}`;
    setDivisions((prev) => [...prev, { id, name: newDivName.trim() }]);
    onDivisionCreate?.(newDivName.trim());
    setNewDivName("");
  }, [newDivName, onDivisionCreate]);

  const createHeat = useCallback(() => {
    if (!newHeatName.trim() || !newHeatDiv) return;
    const id = `heat-${Date.now()}`;
    setHeats((prev) => [...prev, { id, name: newHeatName.trim(), divisionId: newHeatDiv, maxAttempts: newHeatAttempts, riders: [] }]);
    onHeatCreate?.(newHeatName.trim(), newHeatDiv);
    setNewHeatName("");
  }, [newHeatName, newHeatDiv, newHeatAttempts, onHeatCreate]);

  const addRider = useCallback((heatId: string, rider: MockSetupRider) => {
    setHeats((prev) => prev.map((h) =>
      h.id === heatId ? { ...h, riders: [...h.riders, { id: rider.id, name: rider.name, nickname: rider.nickname }] } : h
    ));
    onRiderAdded?.(heatId, rider.id);
  }, [onRiderAdded]);

  const sendInvite = useCallback(() => {
    if (!inviteEmail.trim()) return;
    setSentInvites((prev) => [...prev, { email: inviteEmail.trim(), role: inviteRole }]);
    onInviteSent?.(inviteEmail.trim(), inviteRole);
    setInviteEmail("");
  }, [inviteEmail, inviteRole, onInviteSent]);

  // Group heats by division
  const divisionMap = new Map<string, MockSetupHeat[]>();
  for (const heat of heats) {
    if (!divisionMap.has(heat.divisionId)) divisionMap.set(heat.divisionId, []);
    divisionMap.get(heat.divisionId)!.push(heat);
  }

  return (
    <div>
      {/* Page header matching real Setup */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Setup: Boardstock Invitational</h1>
          <p className="page-subtitle">Configure divisions, heats, assign riders, invite judges</p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        {/* ── Left column: Divisions + Heats ── */}
        <div className="flex-col gap-6">
          {/* Divisions */}
          <div className="card">
            <div className="card-header"><div className="card-title">Divisions</div></div>
            <div className="flex gap-2" style={{ marginBottom: 12 }}>
              <Input placeholder="Division name (e.g. Men's Open)" value={newDivName}
                onChange={(e) => setNewDivName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createDivision()} />
              <Button onClick={createDivision} disabled={!newDivName.trim()}>Add</Button>
            </div>
            {divisions.length === 0 ? (
              <div className="text-sm text-muted" style={{ padding: "8px 0" }}>No divisions yet</div>
            ) : (
              <div className="flex-col gap-2">
                {divisions.map((d) => (
                  <div key={d.id} className="flex items-center justify-between"
                    style={{ padding: "6px 0", borderBottom: "1px solid var(--c-border)" }}>
                    <span style={{ fontWeight: 600 }}>{d.name}</span>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm">Edit</Button>
                      <Button variant="danger" size="sm">Delete</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Heats */}
          <div className="card">
            <div className="card-header"><div className="card-title">Heats</div></div>
            <div style={{ marginBottom: 16 }}>
              <div className="flex gap-2" style={{ marginBottom: 8 }}>
                <Input placeholder="Heat name..." value={newHeatName}
                  onChange={(e) => setNewHeatName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && createHeat()} />
                <Button onClick={createHeat} disabled={!newHeatName.trim() || !newHeatDiv}>Add</Button>
              </div>
              <div className="flex gap-3" style={{ marginTop: 8 }}>
                <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="field-label" style={{ fontSize: 11 }}>Division</label>
                  {divisions.length === 0 ? (
                    <div className="text-sm text-muted" style={{ padding: "6px 0" }}>Create a division first</div>
                  ) : (
                    <select value={newHeatDiv} onChange={(e) => setNewHeatDiv(e.target.value)}
                      style={{ width: "100%", padding: "6px 8px", background: "var(--c-surface-2)", border: "1px solid var(--c-border)", color: "var(--c-text)", fontSize: 13 }}>
                      <option value="">Select a division...</option>
                      {divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  )}
                </div>
                <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="field-label" style={{ fontSize: 11 }}>Max Attempts</label>
                  <div style={{ display: "flex", gap: 4 }}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                      <button key={n}
                        className={`btn btn-sm ${newHeatAttempts === n ? "btn-primary" : "btn-secondary"}`}
                        style={{ flex: "1 1 0", minWidth: 0, padding: "4px 0", fontSize: 11 }}
                        onClick={() => setNewHeatAttempts(n)}>{n}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {heats.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-title">No heats</div>
                <div className="empty-state-desc">Create your first heat above.</div>
              </div>
            ) : (
              <div className="flex-col gap-2">
                {Array.from(divisionMap.entries()).map(([divId, divHeats]) => {
                  const div = divisions.find((d) => d.id === divId);
                  return (
                    <div key={divId}>
                      {div && (
                        <div className="text-sm text-muted" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, marginTop: 8, fontWeight: 600 }}>
                          {div.name}
                        </div>
                      )}
                      {divHeats.map((heat) => {
                        const isExpanded = expandedHeat === heat.id;
                        return (
                          <div key={heat.id} className="card" style={{ padding: 0, marginBottom: 4 }}>
                            <button onClick={() => setExpandedHeat(isExpanded ? null : heat.id)}
                              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: 12, background: "transparent", border: "none", color: "var(--c-text)", cursor: "pointer", textAlign: "left" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 12, transition: "transform 0.15s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}>&#9654;</span>
                                <span style={{ fontWeight: 700 }}>{heat.name}</span>
                              </div>
                              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                <span className="badge badge-off">Max: {heat.maxAttempts > 1 ? `${heat.maxAttempts} attempts` : "10"}</span>
                                <Button variant="danger" size="sm">Delete</Button>
                              </div>
                            </button>
                            {isExpanded && (
                              <div style={{ padding: "0 12px 12px", borderTop: "1px solid var(--c-border)" }}>
                                <div className="text-sm text-muted" style={{ marginBottom: 8, marginTop: 12 }}>Riders in heat:</div>
                                {heat.riders.length === 0 ? (
                                  <div className="text-sm text-muted" style={{ fontStyle: "italic", marginBottom: 8 }}>No riders yet</div>
                                ) : (
                                  heat.riders.map((r) => (
                                    <div key={r.id} className="flex items-center justify-between" style={{ padding: "4px 0" }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--c-surface-3, #2a2a32)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 12, border: "1px solid var(--c-border)" }}>
                                          {r.name.charAt(0)}
                                        </div>
                                        <div>
                                          <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
                                          {r.nickname && <div className="text-muted" style={{ fontSize: 11 }}>&ldquo;{r.nickname}&rdquo;</div>}
                                        </div>
                                      </div>
                                      <Button variant="ghost" size="sm">Remove</Button>
                                    </div>
                                  ))
                                )}
                                <div className="text-sm text-muted" style={{ marginTop: 12, marginBottom: 8 }}>Add rider:</div>
                                {availableRiders.filter((r) => !heat.riders.some((hr) => hr.id === r.id)).map((r) => (
                                  <div key={r.id} className="flex items-center justify-between" style={{ padding: "4px 0" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--c-surface-3, #2a2a32)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 12, border: "1px solid var(--c-border)" }}>
                                        {r.name.charAt(0)}
                                      </div>
                                      <div>
                                        <span className="text-muted" style={{ fontSize: 13 }}>{r.name}</span>
                                        {r.nickname && <span className="text-muted" style={{ fontSize: 11, marginLeft: 4 }}>({r.nickname})</span>}
                                        {r.level && <div className="text-muted" style={{ fontSize: 10 }}>{r.level}</div>}
                                      </div>
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={() => addRider(heat.id, r)}>Add</Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Right column: Settings + Invite ── */}
        <div className="flex-col gap-6">
          {/* Competition Settings */}
          <div className="card">
            <div className="card-header"><div className="card-title">Competition Settings</div></div>
            <div className="field" style={{ marginBottom: 12 }}>
              <label className="field-label">Scoring Model</label>
              <select disabled style={{ width: "100%", padding: "8px 10px", background: "var(--c-surface-2)", border: "1px solid var(--c-border)", color: "var(--c-text)", fontSize: 13 }}>
                <option>Best Score (Max)</option>
              </select>
              <div className="text-sm text-muted" style={{ marginTop: 6 }}>Rider keeps only their highest judge score</div>
            </div>
            <div className="text-sm text-muted" style={{ marginBottom: 16 }}>Judges: 3 (set at creation, cannot be changed)</div>
            <div style={{ marginBottom: 16 }}>
              <label className="field-label" style={{ marginBottom: 8 }}>Overlay Defaults (applied on Go Live)</label>
              <div className="flex-col gap-2">
                {["Show heat name banner", "Show heat riders", "Show rider scores", "Show leaderboard"].map((label) => (
                  <label key={label} className="flex items-center gap-2" style={{ fontSize: 13, cursor: "default" }}>
                    <input type="checkbox" checked={label.includes("banner") || label.includes("heat riders")} disabled style={{ accentColor: "var(--c-accent)" }} />
                    {label}
                  </label>
                ))}
              </div>
              <div className="field" style={{ marginTop: 8, marginBottom: 0 }}>
                <label className="field-label" style={{ fontSize: 11 }}>Default overlay style</label>
                <select disabled style={{ width: "100%", padding: "6px 8px", background: "var(--c-surface-2)", border: "1px solid var(--c-border)", color: "var(--c-text)", fontSize: 13 }}>
                  <option>Broadcast</option>
                </select>
              </div>
            </div>
          </div>

          {/* Invite Team */}
          <div className="card">
            <div className="card-header"><div className="card-title">Invite Team</div></div>
            <div className="flex gap-2" style={{ marginBottom: 8 }}>
              <Input placeholder="Email address" value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendInvite()} style={{ flex: 1 }} />
              <Button onClick={sendInvite} disabled={!inviteEmail.trim()}>Invite</Button>
            </div>
            <div className="flex gap-3" style={{ marginBottom: 12 }}>
              {["judge", "producer", "admin"].map((role) => (
                <label key={role} className="flex items-center gap-2" style={{ cursor: "pointer", fontSize: 13 }}>
                  <input type="checkbox" checked={inviteRole === role} onChange={() => setInviteRole(role)} style={{ accentColor: role === "judge" ? "#22c55e" : role === "producer" ? "#a855f7" : "#3b82f6" }} />
                  <span style={{ textTransform: "capitalize" }}>{role}</span>
                </label>
              ))}
            </div>
            {sentInvites.length > 0 && (
              <div className="flex-col gap-1">
                {sentInvites.map((inv, i) => (
                  <div key={i} className="flex items-center justify-between text-sm" style={{ padding: "4px 0" }}>
                    <span className="text-mono">{inv.email}</span>
                    <span className="badge" style={{ background: inv.role === "judge" ? "#22c55e" : inv.role === "producer" ? "#a855f7" : "#3b82f6", color: "#fff", textTransform: "capitalize", fontSize: 11 }}>{inv.role}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
